import React, { useEffect, useMemo, useRef, useState } from 'react';
// Reuse the character selector's combobox + modal styling so the two editors
// look identical. Transform-specific layout lives in TransformEditor.css.
import '../character/CharacterSelector.css';

// Case/diacritic-insensitive, numeric-aware sort by name.
const sortTransforms = (list) =>
    [...(list || [])].sort((a, b) =>
        (a?.name || '').trim().localeCompare((b?.name || '').trim(), undefined, { sensitivity: 'base', numeric: true })
    );

/**
 * Searchable transform picker. Mirrors the character manager's combobox: type to
 * filter, arrow keys to move, Enter to choose, and a gold "Create New" row on top.
 */
export default function TransformSelector({ transforms, currentName, onChoose, onCreateNew, disabled = false }) {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState(currentName || '');
    const [highlight, setHighlight] = useState(0);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const listId = 'transform-listbox';

    const sorted = useMemo(() => sortTransforms(transforms), [transforms]);

    useEffect(() => { setQuery(currentName || ''); }, [currentName]);

    const resetToCurrent = () => setQuery(currentName || '');
    const closePanel = ({ reset = false, blur = false } = {}) => {
        setIsOpen(false);
        if (reset) resetToCurrent();
        if (blur) inputRef.current?.blur();
    };

    useEffect(() => {
        const onDocClick = (e) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target)) closePanel({ reset: true, blur: true });
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentName]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return sorted;
        return sorted.filter((t) => (t?.name || '').toLowerCase().includes(q));
    }, [sorted, query]);

    useEffect(() => {
        if (!isOpen) return;
        setHighlight((h) => Math.max(0, Math.min(h, filtered.length)));
    }, [isOpen, filtered.length]);

    const open = () => {
        setIsOpen(true);
        if (document.activeElement !== inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    // Opening the picker clears the current text and shows the full list, so you
    // can jump to a different transform without first deleting the selected name.
    const openFresh = () => {
        open();
        setQuery('');
        setHighlight(0);
    };

    const chooseIndex = (idx) => {
        if (idx === 0) {
            setQuery('');
            closePanel({ blur: true });
            onCreateNew?.();
            return;
        }
        const item = filtered[idx - 1];
        if (!item) return;
        setQuery(item.name);
        onChoose?.(item);
        closePanel({ blur: true });
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIsOpen(true);
            setHighlight((h) => Math.min(h + 1, filtered.length));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setIsOpen(true);
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            chooseIndex(highlight);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            closePanel({ reset: true, blur: false });
        }
    };

    return (
        <div
            className="combo combo--two-col"
            ref={containerRef}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) closePanel({ reset: true, blur: false });
            }}
        >
            <div className="combo-input-wrap">
                <input
                    ref={inputRef}
                    type="text"
                    className="combo-input"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    aria-autocomplete="list"
                    placeholder="Select a Transform…"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setHighlight(0); }}
                    onFocus={openFresh}
                    onClick={openFresh}
                    onKeyDown={onKeyDown}
                    disabled={disabled}
                />
                <button
                    type="button"
                    className={`combo-caret ${isOpen ? 'is-open' : ''}`}
                    aria-label={isOpen ? 'Close transform list' : 'Open transform list'}
                    onClick={() => (isOpen ? closePanel({ reset: true, blur: false }) : open())}
                    disabled={disabled}
                />
            </div>

            {isOpen && (
                <div className="combo-panel" role="listbox" id={listId} tabIndex={-1}>
                    <div
                        className={'combo-option create ' + (highlight === 0 ? 'is-highlighted' : '')}
                        role="option"
                        aria-selected={highlight === 0}
                        onMouseEnter={() => setHighlight(0)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => chooseIndex(0)}
                    >
                        <span className="name">+ Create New Transform…</span>
                        <span className="player"> </span>
                    </div>

                    {filtered.map((t, i) => {
                        const idx = i + 1;
                        return (
                            <div
                                key={t.transformId}
                                className={'combo-option ' + (highlight === idx ? 'is-highlighted' : '')}
                                role="option"
                                aria-selected={highlight === idx}
                                onMouseEnter={() => setHighlight(idx)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => chooseIndex(idx)}
                                title={t.name}
                            >
                                <span className="name">{t.name}</span>
                                <span className="player">{(t.abilities?.length ?? 0)} abilities</span>
                            </div>
                        );
                    })}

                    {!filtered.length && (
                        <div className="combo-empty">No matches. Press Enter to create.</div>
                    )}
                </div>
            )}
        </div>
    );
}
